import React, { useState, useEffect } from "react";
import { useFormik } from "formik";
import TextField from "@mui/material/TextField";
import { useSnackbar } from "notistack";
import MyLocationOutlinedIcon from "@mui/icons-material/MyLocationOutlined";
import {
  Box,
  Button,
  CircularProgress,
  Grid,
} from "@mui/material";
import { useLazyGetInstanceQuery } from "../../../redux/api/instanceApi";
import customTheme from "../../../theme/customTheme";
import CommonDialog from "../../../components/Dialog/CommonDialog";
import { useLazyGetReaderSerialMapQuery } from "../../../redux/api/readerAppApi";
import { useCreateErrorMessage } from "../../../hooks/useCreateErrorMessage";
import { FindDeviceProps, ReaderData } from "./types";
import FoundDevicesList from "./FoundDevicesList";

const initialValues = {
  search: "",
};

export default function FindDevice({
  isOpen,
  handleCloseDialog,
}: FindDeviceProps) {
  const [instanceIds, setInstanceIds] = useState<Set<number>>(new Set());
  const [readerDatas, setReaderDatas] = useState<ReaderData[]>([]);
  const [readerSerial, setReaderSerial] = useState<string>("");
  const [
    readerSerialMapQueryTrigger,
    readerSerialMapQueryResult
  ] = useLazyGetReaderSerialMapQuery();
  const [
    instanceQueryTrigger,
    instanceQueryResult
  ] = useLazyGetInstanceQuery();
  const { createErrorMessage } = useCreateErrorMessage();
  const { enqueueSnackbar } = useSnackbar();
  const {
    data: readerSerialMapQueryData,
    error: readerSerialMapQueryError,
    isError: isErrorReaderSerialMapQuery,
    isFetching: isFetchingReaderSerialMapQuery,
  } = readerSerialMapQueryResult;
  const {
    data: instanceQueryData,
    error: instanceQueryError,
    isError: isErrorInstanceQuery,
    isFetching: isFetchingInstanceQuery,
    isSuccess: isSuccessInstanceQuery
  } = instanceQueryResult;

  const isFetching = (isFetchingReaderSerialMapQuery || isFetchingInstanceQuery);
  const isError = (isErrorInstanceQuery || isErrorReaderSerialMapQuery);
  const isSuccess = isSuccessInstanceQuery;
  const error = (
    readerSerialMapQueryError ||
    instanceQueryError ||
    null
  );

  const handleInstanceQuery = (ids: Set<number>) => instanceQueryTrigger({
    debounceValue: "",
    rowsPerPage: 1000,
    ids: Array.from(ids),
  })

  useEffect(() => {
    if (isSuccess) {
      enqueueSnackbar("Device(s) found with the serial number", {
        variant: "success",
        autoHideDuration: 2000,
      });
    } else if (isError) {
      createErrorMessage({
        error,
        message: "Failed to find the device with the serial number",
      });
    }
    // eslint-ignore-next-line react-hooks/exhaustive-deps
  }, [isSuccess, isError])

  useEffect(() => {
    if (readerSerialMapQueryData) {
      const { results: readerSerialMapResults = [] } = readerSerialMapQueryData;
      const ids: Set<number> = new Set();
      const newReaderData: ReaderData[] = [];
      readerSerialMapResults.forEach(({ serial_number: serial, instance }) => {
        ids.add(instance);
        newReaderData.push({
          instance,
          serial,
          instanceName: "",
          instanceAddress: "",
        })
      });

      if (ids.size !== instanceIds.size  || !ids.isSubsetOf(instanceIds)) {
        setInstanceIds(ids);
        handleInstanceQuery(ids);
      }

      const { results: instanceResults = [] } = instanceQueryData || {};
      const instanceDataMap: Record<number, { name: string, address: string }> = {};
      instanceResults.forEach(({ id, name, domain, subdomain }) => {
        instanceDataMap[id] = {
          name,
          address: `${subdomain}.${domain}`,
        }
      });

      for (let i = 0; i < newReaderData.length; i += 1) {
        const readerData = newReaderData[i];
        const { instance } = readerData;
        const { name = "", address = "" } = instanceDataMap[instance] || {};
        readerData.instanceName = name;
        readerData.instanceAddress = address;
      };

      setReaderDatas(newReaderData);
    }
    // eslint-ignore-next-line react-hooks/exhaustive-deps
  }, [readerSerialMapQueryData, instanceQueryData]);

  const handleReaderSerialMapQuery = (serial = "") => {
    setReaderSerial(serial);
    readerSerialMapQueryTrigger({
      serial,
      pageSize: 1000,
    })
  }

  const formik = useFormik({
    enableReinitialize: true,
    initialValues,
    onSubmit: (values) => {
      handleReaderSerialMapQuery(values.search);
    },
  });

  return (
    <CommonDialog
      isOpen={isOpen}
      onClose={handleCloseDialog}
      title="Find Device"
    >
      <form onSubmit={formik.handleSubmit}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              required
              fullWidth
              id="search"
              name="search"
              label="Device Serial Number"
              value={formik.values.search}
              onChange={formik.handleChange}
            />
          </Grid>
          <FoundDevicesList
            data={readerDatas}
            serial={isFetching ? "" : readerSerial}
          />
          <Grid item xs={12}>
            <Grid container spacing={2}>
              <Grid item>
                <Button
                  variant="contained"
                  type="submit"
                  disabled={isFetching}
                  size="large"
                >
                  {isFetching ? (
                    <CircularProgress
                      size="1.5rem"
                      sx={{
                        color: customTheme.palette.common.white,
                        marginRight: "0.5rem",
                      }}
                    />
                  ) : (
                    <MyLocationOutlinedIcon sx={{ marginRight: "0.5rem" }} />
                  )}
                  <Box
                    component="span"
                    sx={{
                      color: customTheme.palette.common.white,
                    }}
                  >
                    Find Device
                  </Box>
                </Button>
              </Grid>
              <Grid item>
                <Button
                  variant="outlined"
                  onClick={handleCloseDialog}
                  size="large"
                >
                  Cancel
                </Button>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </form>
    </CommonDialog>
  );
}
